import * as React from 'react';
import {
  useTab,
  useTabList,
  useTabPanel,
  useTabListState,
  Item,
  useCollection,
  ReactAriaListCollection as ListCollection,
} from '@sail/react-aria';
import type {AriaTabPanelProps, TabListState} from '@sail/react-aria';
import type {CollectionChildren, Key, Node} from '@react-types/shared';

import {provider} from '@sail/engine';
import * as Theme from '@sail/theme-classic';
import {chevronDown} from '@sail/icons/react/Icon';
import {Link} from './Link';
import {Menu, MenuItem} from './Menu';
import {view} from '../view';
import {css} from '../css';
import {useResizeObserver} from '../../components/hooks/useResizeObserver';
import {Icon} from './Icon';

const {createContext, useContext, useState, useRef, useEffect, useCallback} =
  React;

function toNumber(value: string) {
  const number = parseInt(value, 10);
  return isNaN(number) ? 0 : number;
}

const menuContainerStyles = css({
  padding: 'xsmall',
  borderBottom: css.value('1px solid'),
  borderColor: 'neutral',
});

const linkStyles = css({
  layout: 'row',
  alignX: 'center',
  alignY: 'center',
  flexGrow: css.value(1),
});

const chevronStyles = css({fill: 'brand', marginLeft: 'small'});

/**
 * @external
 */
interface TabProps {
  /**
   * @external
   * Whether or not the tab should be disabled
   */
  disabled?: boolean;
  /**
   * @external
   * Children passed to Tab component
   */
  children: React.ReactNode;
}

/**
 * @external
 */
export const Tab = Item as (props: TabProps) => JSX.Element;

// We don't want react-aria's polymorphic Item to show up in the type definition
/**
 * @external
 * @param key
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TabPanel = Item as (props: any) => JSX.Element;

/**
 * @external
 */
interface TabsProps {
  /**
   * @external
   * Children passed to Tabs component
   */
  children: React.ReactNode;

  /**
   * @external
   * Size of Tabs component, can be 'small', 'medium' or 'large'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * @external
   * Whether or not the tab list should take up the entire width of its container
   */
  fitted?: boolean;

  /**
   * @external
   * Key of currently selected Tab panel if controlling the component
   */
  selectedKey?: Key;

  /**
   * @external
   * Callback to be fired when a Tab is selected
   */
  onSelectionChange?: (arg: Key) => void;

  dir?: string;
}

interface TabContextValue {
  state: {
    tabListState?: TabListState<TabProps>;
    setTabListState?: (arg: TabListState<TabProps>) => void;
  };
  style: {
    size: TabsProps['size'];
    fitted: TabsProps['fitted'];
  };
  tabProps: {
    selectedKey?: TabsProps['selectedKey'];
    onSelectionChange?: TabsProps['onSelectionChange'];
  };
  dir: TabsProps['dir'];
}

const TabContext = createContext<TabContextValue>({
  state: {},
  style: {size: 'medium', fitted: false},
  tabProps: {},
  dir: 'ltr',
});

function useTabContext(): TabContextValue {
  return useContext(TabContext);
}

/**
 * @external
 */
export const Tabs = view.div.create<TabsProps>(
  (
    {
      children,
      selectedKey: rawSelectedKey,
      onSelectionChange,
      size = 'medium',
      fitted = false,
      dir = 'ltr',
      ...props
    },
    View,
  ) => {
    const [tabListState, setTabListState] = useState<TabListState<TabProps>>();

    /**
     * React coerces keys to strings, and the internal react-stately tabs map is
     * built from those keys. If set, we need to coerce selectedKey to a
     * string to ensure that the key is present in the map on initial render.
     */
    const selectedKey = rawSelectedKey?.toString() ?? rawSelectedKey;

    const value = {
      state: {tabListState, setTabListState},
      style: {size, fitted},
      tabProps: {
        selectedKey,
        onSelectionChange,
      },
      dir,
    };

    return <View uses={[provider(TabContext, value), props]}>{children}</View>;
  },
);

interface InternalTabProps {
  item: Node<TabProps>;
  state: TabListState<TabProps>;
  disabled?: boolean;
}

const InternalTab = view.div.create<InternalTabProps>(
  ({item, state, disabled}, View) => {
    const ref = useRef<HTMLDivElement>(null);
    const {
      style: {size, fitted},
      state: {setTabListState, tabListState},
    } = useTabContext();
    const {key, rendered} = item;
    const {tabProps} = useTab({key, isDisabled: disabled}, state, ref);
    const selected = state.selectedItem === item;

    useEffect(() => {
      if (tabListState != null && setTabListState) {
        if (disabled) {
          setTabListState({
            ...tabListState,
            disabledKeys: tabListState.disabledKeys?.add(key),
          });
        } else if (tabListState.disabledKeys?.delete(key)) {
          setTabListState({
            ...tabListState,
            disabledKeys: tabListState.disabledKeys,
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabListState?.disabledKeys, setTabListState, key, disabled]);

    return (
      <View
        ref={ref}
        uses={[
          tabProps,
          Theme.Tab,
          Theme.TabVariants.assign({
            size,
            fitted,
            selected,
            disabled,
          }),
        ]}
      >
        {rendered}
        {disabled ? <view.div uses={[Theme.TabDisabledOverlay]} /> : null}
      </View>
    );
  },
);

/**
 * @external
 */
export const TabList = view.div.create<{
  children: CollectionChildren<TabProps>;
}>((props, View) => {
  const ref = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tabLineRef = useRef<HTMLDivElement>(null);

  const {
    style: {fitted},
    state,
    tabProps,
    dir,
  } = useTabContext();

  const {setTabListState} = state;

  const localState = useTabListState({
    ...tabProps,
    children: props.children,
  });

  const {tabListProps} = useTabList({...tabProps, ...props}, localState, ref);

  const measureElement = useCallback(
    (element: HTMLElement) => {
      // eslint-disable-next-line @sail/ui-no-reflow
      const rect = element.getBoundingClientRect();
      // eslint-disable-next-line @sail/ui-no-reflow
      const style = window.getComputedStyle(element);

      const measurement = {
        contentSize:
          // eslint-disable-next-line @sail/ui-no-reflow
          element.clientWidth -
          toNumber(style.paddingLeft) -
          toNumber(style.paddingRight),
        size: rect.width,
        start: rect.left,
        end: rect.right,
      };

      if (dir === 'rtl') {
        const {start, end} = measurement;
        measurement.start = end;
        measurement.end = start;
      }

      return measurement;
    },
    [dir],
  );

  const checkShouldCollapse = useCallback(() => {
    if (wrapperRef.current) {
      const parentMeasurement = measureElement(wrapperRef.current);
      const tabs = [...wrapperRef.current.querySelectorAll('[role="tab"]')];
      const tabList = wrapperRef.current.querySelector(
        '[data-id="tab-list-wrapper"]',
      );
      const trigger = wrapperRef.current.querySelector(
        '[data-id="menu-container"]',
      );

      // show all the children so we can measure them
      (tabList as HTMLElement).style.display = '';

      // hide trigger by default
      (trigger as HTMLElement).style.display = 'none';

      const tabEntries = tabs.map((child, index) => {
        const element = child as HTMLElement;

        return {
          element,
          index,
          measurement: measureElement(element),
        };
      });

      const {length, 0: first, [length - 1]: last} = tabEntries;

      // closesness in value between contentSpace and availableSpace were causing the tabs to show and hide very rapidly
      // rounding in this way prevents that
      const contentSpace = Math.floor(
        Math.abs(last.measurement.end - first.measurement.start),
      );
      const availableSpace = Math.ceil(parentMeasurement.size);

      const shouldCollapse = contentSpace > availableSpace;

      if (shouldCollapse) {
        (tabList as HTMLElement).style.display = 'none';
        (trigger as HTMLElement).style.display = '';
      } else {
        (trigger as HTMLElement).style.display = 'none';
      }
    }
  }, [wrapperRef, measureElement]);

  const applyTabLineStyles = useCallback(() => {
    if (wrapperRef.current) {
      const selectedTab: HTMLElement | null = wrapperRef.current.querySelector(
        `[data-key="${localState.selectedKey}"]`,
      );

      const tabLine = tabLineRef.current;
      if (selectedTab != null && tabLine) {
        // eslint-disable-next-line @sail/ui-no-reflow
        tabLine.style.width = `${selectedTab.offsetWidth}px`;
        // eslint-disable-next-line @sail/ui-no-reflow
        tabLine.style.transform = `translateX(${selectedTab.offsetLeft}px)`;

        // Do this after the above properties set so the initial
        // layout doesn't animate
        requestAnimationFrame(() => {
          tabLine.style.transition = 'transform 250ms';
        });
      }
    }
  }, [wrapperRef, localState.selectedKey]);

  useResizeObserver({
    node: wrapperRef,
    handler: useCallback(() => {
      checkShouldCollapse();
      applyTabLineStyles();
    }, [checkShouldCollapse, applyTabLineStyles]),
  });

  useEffect(() => {
    setTabListState?.(localState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localState.disabledKeys,
    localState.selectedItem,
    localState.selectedKey,
  ]);

  return (
    <View ref={wrapperRef}>
      <view.div
        data-id="tab-list-wrapper"
        css={{
          position: css.value('relative'),
        }}
      >
        <view.div
          ref={ref}
          uses={[
            tabListProps,
            Theme.TabList,
            Theme.TabListVariants.assign({fitted}),
          ]}
        >
          {[...localState.collection].map((item) => (
            <InternalTab
              key={item.key}
              item={item}
              disabled={item.props.disabled}
              state={localState}
            />
          ))}
        </view.div>
        <view.div role="presentation" ref={tabLineRef} uses={[Theme.TabLine]} />
      </view.div>

      <view.div data-id="menu-container" uses={[menuContainerStyles]}>
        <Menu
          trigger={
            <Link uses={[linkStyles]}>
              {localState?.selectedItem?.rendered}
              <Icon icon={chevronDown} size="small" uses={[chevronStyles]} />
            </Link>
          }
          onAction={(key) => localState.setSelectedKey(key)}
        >
          {[...localState.collection].map(({key, rendered, props}) => {
            return (
              <MenuItem key={key} id={key.toString()} disabled={props.disabled}>
                {rendered}
              </MenuItem>
            );
          })}
        </Menu>
      </view.div>
    </View>
  );
});

const InternalTabPanel = view.div.create<AriaTabPanelProps>(
  ({children, ...props}, View) => {
    const {state} = useTabContext();
    const {tabListState} = state;
    const ref = useRef<HTMLDivElement>(null);

    const {tabPanelProps} = useTabPanel(props, tabListState!, ref);

    return (
      <View ref={ref} uses={[tabPanelProps]}>
        {children}
      </View>
    );
  },
);

/**
 * @external
 */
export const TabPanels = view.div.create<{
  children: CollectionChildren<AriaTabPanelProps>;
}>(({children}) => {
  const {state} = useTabContext();
  const {tabListState} = state;
  const ref = useRef<HTMLDivElement>(null);

  const collection = useCollection(
    {children},
    (nodes) => new ListCollection(nodes),
    {suppressTextValueWarning: true},
  );
  const selectedItem = tabListState
    ? collection.getItem(tabListState.selectedKey)
    : null;

  return (
    <InternalTabPanel ref={ref} key={tabListState?.selectedKey}>
      {selectedItem && selectedItem.props.children}
    </InternalTabPanel>
  );
});
